/**
 * Per-speaker portrait FRAMING (scale + pan), synced across every device.
 *
 * Sources of truth (highest → lowest wins on read):
 *   1. Local edits made in this browser session (live re-render on the tile)
 *   2. Remote framing from `GET /api/speaker-framing` (populated once at boot)
 *   3. Fallback: no override → SpeakerCard uses its own default
 *
 * Writes:
 *   - Optimistic local update fires the `PORTRAIT_ZOOM_CHANGE_EVENT` so tiles
 *     re-render instantly.
 *   - Attempts a `PUT /api/speaker-framing/:name` — only succeeds when the
 *     user has a valid admin session (JWT auto-attached elsewhere). Non-admins
 *     still get the immediate local preview.
 *
 * Back-compat: legacy numeric localStorage values are still read as scale.
 */
const KEY = 'vaad_speaker_zoom_overrides_v1';
export const PORTRAIT_ZOOM_CHANGE_EVENT = 'vaad-portrait-zoom-changed';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_POS_X = 50;
const DEFAULT_POS_Y = 22;

const clampScale = (n) => Math.max(0.9, Math.min(3.0, Number(n) || 1));
const clampRotate = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(-45, Math.min(45, v));
};
const clampPos = (n, fallback) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(100, v));
};

// ── Local (per-browser) store ────────────────────────────────────────────────
const loadLocal = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; }
  catch { return {}; }
};
const saveLocal = (obj) => {
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch { /* quota */ }
  try { window.dispatchEvent(new Event(PORTRAIT_ZOOM_CHANGE_EVENT)); } catch { /* ignore */ }
};

// ── Remote map (fetched once at boot, refreshable) ───────────────────────────
let remote = {};
let remoteFetched = false;
let remoteInFlight = null;

// ── Auth-refresh helper ──────────────────────────────────────────────────────
// The access_token cookie expires after 15 minutes. Admin edits made after
// that silently 401'd — so we transparently hit /api/auth/refresh once and
// retry the original request. If refresh itself 401s the refresh_token is
// gone (7-day expiry or logged out) and we surface a "session expired"
// event so App.js can show a re-login toast.
let refreshInFlight = null;
let sessionExpired = false;

async function refreshAccessToken() {
  if (sessionExpired) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (r.ok) return true;
      // 401 here => refresh_token expired or missing — full re-login needed.
      if (r.status === 401) sessionExpired = true;
      return false;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * fetch() wrapper that transparently retries once after refreshing the
 * access_token when it 401s. Returns the final Response (from the retry
 * if a refresh succeeded, or from the original 401 if it didn't) plus a
 * `sessionExpired` flag callers can inspect.
 */
async function fetchWithAuthRetry(url, options = {}) {
  const opts = { credentials: 'include', ...options };
  let r;
  try {
    r = await fetch(url, opts);
  } catch (e) {
    throw e;
  }
  if (r.status !== 401) return { response: r, sessionExpired: false };
  // Try to refresh, then retry once.
  const refreshed = await refreshAccessToken();
  if (!refreshed) return { response: r, sessionExpired };
  try {
    const r2 = await fetch(url, opts);
    return { response: r2, sessionExpired: false };
  } catch (e) {
    throw e;
  }
}

const fetchRemote = () => {
  if (remoteInFlight) return remoteInFlight;
  remoteInFlight = (async () => {
    try {
      const r = await fetch(`${API}/api/speaker-framing`, { credentials: 'include' });
      if (r.ok) remote = await r.json() || {};
    } catch { /* offline is fine — local still works */ }
    remoteFetched = true;
    remoteInFlight = null;
    try { window.dispatchEvent(new Event(PORTRAIT_ZOOM_CHANGE_EVENT)); } catch { /* ignore */ }
  })();
  return remoteInFlight;
};

// Kick off the fetch on module load so tiles paint with server values on first render.
if (typeof window !== 'undefined') {
  fetchRemote();
}

const normalizeEntry = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return { scale: clampScale(raw), posX: DEFAULT_POS_X, posY: DEFAULT_POS_Y, rotate: 0 };
  }
  if (typeof raw === 'object') {
    return {
      scale: clampScale(raw.scale ?? 1),
      posX: clampPos(raw.posX, DEFAULT_POS_X),
      posY: clampPos(raw.posY, DEFAULT_POS_Y),
      rotate: clampRotate(raw.rotate ?? 0),
    };
  }
  return null;
};

/** Merged framing for a speaker — local override wins over server override. */
export function getSpeakerFraming(speakerName) {
  if (!speakerName) return null;
  const local = normalizeEntry(loadLocal()[speakerName]);
  if (local) return local;
  const server = normalizeEntry(remote[speakerName]);
  return server;
}

/** Legacy accessor kept so existing call sites still compile — returns scale only. */
export function getSpeakerZoomOverride(speakerName) {
  const f = getSpeakerFraming(speakerName);
  return f ? f.scale : null;
}

/** Merge patch → persist locally AND fire-and-forget push to server. */
export function updateSpeakerFraming(speakerName, patch) {
  if (!speakerName) return;
  const all = loadLocal();
  const current = normalizeEntry(all[speakerName]) || normalizeEntry(remote[speakerName]) || {
    scale: 1, posX: DEFAULT_POS_X, posY: DEFAULT_POS_Y, rotate: 0,
  };
  const next = {
    scale: patch.scale != null ? clampScale(patch.scale) : current.scale,
    posX: patch.posX != null ? clampPos(patch.posX, current.posX) : current.posX,
    posY: patch.posY != null ? clampPos(patch.posY, current.posY) : current.posY,
    rotate: patch.rotate != null ? clampRotate(patch.rotate) : (current.rotate ?? 0),
  };
  all[speakerName] = next;
  saveLocal(all);
  // Also update the in-memory remote cache so a same-session re-fetch stays in sync.
  remote[speakerName] = next;
  pushToServer(speakerName, next);
}

/** Back-compat wrapper — scale-only writers. */
export function setSpeakerZoomOverride(speakerName, scale) {
  updateSpeakerFraming(speakerName, { scale });
}

export function clearSpeakerZoomOverride(speakerName) {
  if (!speakerName) return;
  const all = loadLocal();
  delete all[speakerName];
  saveLocal(all);
  delete remote[speakerName];
  deleteOnServer(speakerName);
}

// ── Server push helpers ──────────────────────────────────────────────────────
// Event fired whenever a server-push succeeds or fails, so the UI can toast.
export const PORTRAIT_ZOOM_SAVE_EVENT = 'vaad-portrait-zoom-save';

async function pushToServer(name, framing) {
  try {
    const { response: r, sessionExpired: expired } = await fetchWithAuthRetry(
      `${API}/api/speaker-framing/${encodeURIComponent(name)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(framing),
      },
    );
    const ok = r.ok;
    try {
      window.dispatchEvent(new CustomEvent(PORTRAIT_ZOOM_SAVE_EVENT, {
        detail: { name, ok, status: r.status, sessionExpired: expired },
      }));
    } catch { /* ignore */ }
  } catch (e) {
    try {
      window.dispatchEvent(new CustomEvent(PORTRAIT_ZOOM_SAVE_EVENT, {
        detail: { name, ok: false, status: 0, error: String(e), sessionExpired: false },
      }));
    } catch { /* ignore */ }
  }
}

async function deleteOnServer(name) {
  try {
    await fetchWithAuthRetry(`${API}/api/speaker-framing/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  } catch { /* offline */ }
}

export function refreshSpeakerFramingFromServer() {
  remoteFetched = false;
  return fetchRemote();
}

export function isSpeakerFramingLoaded() {
  return remoteFetched;
}

/**
 * One-shot: push every localStorage-only framing override to the server so
 * an admin who tuned portraits BEFORE server-sync existed doesn't have to
 * redo them on every device. Idempotent — guarded by a localStorage flag.
 * Silently fails for non-admins (their PUTs 401 and we don't care).
 */
const MIGRATED_FLAG = 'vaad_framing_migrated_to_server_v1';
export async function migrateLocalToServer() {
  try {
    if (localStorage.getItem(MIGRATED_FLAG) === '1') return { skipped: true };
    const local = loadLocal();
    const entries = Object.entries(local);
    if (entries.length === 0) {
      localStorage.setItem(MIGRATED_FLAG, '1');
      return { pushed: 0 };
    }
    let ok = 0;
    for (const [name, raw] of entries) {
      const framing = normalizeEntry(raw);
      if (!framing) continue;
      try {
        const { response: r } = await fetchWithAuthRetry(
          `${API}/api/speaker-framing/${encodeURIComponent(name)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(framing),
          },
        );
        if (r.ok) ok += 1;
      } catch { /* offline */ }
    }
    // Only mark migrated if we actually managed to push at least one — this
    // way a non-admin's localStorage isn't marked "done" prematurely.
    if (ok > 0) localStorage.setItem(MIGRATED_FLAG, '1');
    return { pushed: ok, total: entries.length };
  } catch {
    return { error: true };
  }
}
